import React, { forwardRef, useEffect, useImperativeHandle, useMemo } from 'react';
import { useRowSelect, useSortBy, useTable } from 'react-table';

import {
  EmptyStateContainer,
  HeaderCellWithIcon,
  StyledEmptyStateText,
  StyledTable,
  TableBody,
  TableDataCell,
  TableEmptyState,
  TableFooter,
  TableFooterCell,
  TableFooterRow,
  TableHead,
  TableHeaderCell,
  TablePaginationWrapper,
  TableRow,
  TableScrollWrapper,
  PaginationTitle,
  PaginationControls,
  PaginationLeft,
  PaginationRight,
  PaginationButton,
} from './table.styles';

const InboxIcon = () => (
  <svg fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="1.5">
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      d="M4 13h4l2 3h4l2-3h4M4 13V6a1 1 0 0 1 1-1h14a1 1 0 0 1 1 1v7M4 13l1.4 5.6a1 1 0 0 0 1 .8h11.2a1 1 0 0 0 1-.8L20 13"
    />
  </svg>
);

const ChevronLeftIcon = () => (
  <svg fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2.5">
    <path strokeLinecap="round" strokeLinejoin="round" d="m15 18-6-6 6-6" />
  </svg>
);

const ChevronRightIcon = () => (
  <svg fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2.5">
    <path strokeLinecap="round" strokeLinejoin="round" d="m9 18 6-6-6-6" />
  </svg>
);

const selectionHook = (hooks) => {
  hooks.allColumns.push((columns) => [
    {
      id: '_selector',
      disableResizing: true,
      disableGroupBy: true,
      minWidth: 32,
      width: 32,
      maxWidth: 32,
    },
    ...columns,
  ]);
};

/**
 * Reusable, themed data table built on react-table v7.
 * Mirrors the shared Table component's contract (sorting, row click,
 * optional row selection, and simple previous/next pagination) so it
 * drops into any list screen — Tickets, Users, etc.
 */
const Table = forwardRef((props, ref) => {
  const {
    data,
    columns,
    hasFooter,
    onSortedChange,
    onRowClick,
    canSelect = false,
    onRowsSelect,
    isSortByApi = false,
    emptyStateText = 'No records found',
    notRequireEmptyState = false,
    extraHeaderPadding = true,
    pageSize,
    page,
    prevPage,
    nextPage,
    total = 0,
  } = props;

  const maxPages = Math.max(Math.ceil(total / pageSize), 1);
  const start = total === 0 ? 0 : page * pageSize + 1;
  const end = Math.min((page + 1) * pageSize, total);
  const hasPrev = page > 0;
  const hasNext = page + 1 < maxPages;

  const hooks = useMemo(() => {
    return canSelect ? [useSortBy, useRowSelect, selectionHook] : [useSortBy];
  }, [canSelect]);

  const tableOptions = useMemo(
    () => ({
      columns,
      data,
      ...(isSortByApi ? { manualSortBy: true, autoResetSortBy: false } : {}),
    }),
    [columns, data, isSortByApi]
  );

  const instance = useTable(tableOptions, ...hooks);
  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    rows,
    prepareRow,
    selectedFlatRows,
    state: { sortBy },
    toggleAllRowsSelected,
  } = instance;

  useImperativeHandle(ref, () => ({
    onToggleAllRowsSelected(value) {
      toggleAllRowsSelected(value);
    },
  }));

  useEffect(() => {
    onRowsSelect?.(selectedFlatRows);
  }, [onRowsSelect, selectedFlatRows]);

  useEffect(() => {
    onSortedChange?.(sortBy);
  }, [sortBy, onSortedChange]);

  if (!data?.length) {
    if (notRequireEmptyState) return null;
    return (
      <TableEmptyState>
        <EmptyStateContainer>
          <InboxIcon />
        </EmptyStateContainer>
        <StyledEmptyStateText>{emptyStateText}</StyledEmptyStateText>
      </TableEmptyState>
    );
  }

  const cellClickHandler = (cell) => () => {
    if (
      onRowClick &&
      !cell.column.isGrouped &&
      !cell.row.isGrouped &&
      cell.column.id !== '_selector'
    ) {
      onRowClick(cell.row.original, cell.column.id);
    }
  };

  return (
    <TableScrollWrapper>
      <StyledTable {...getTableProps()}>
        <TableHead>
          {headerGroups.map((headerGroup, headerIndex) => (
            <TableRow {...headerGroup.getHeaderGroupProps()} key={headerIndex}>
              {headerGroup.headers.map((column, colIndex) => (
                <TableHeaderCell
                  {...column.getHeaderProps(
                    column.canSort ? column.getSortByToggleProps() : undefined
                  )}
                  key={colIndex}
                  $width={column.width}
                  $align={column.align}
                  $extraHeaderPadding={extraHeaderPadding}
                  style={column.canSort ? { cursor: 'pointer' } : undefined}
                >
                  <HeaderCellWithIcon>
                    {column.render('Header')}
                    {column.canSort && (
                      <span aria-hidden="true">
                        {column.isSorted ? (column.isSortedDesc ? ' ▾' : ' ▴') : ''}
                      </span>
                    )}
                  </HeaderCellWithIcon>
                </TableHeaderCell>
              ))}
            </TableRow>
          ))}
        </TableHead>

        <TableBody {...getTableBodyProps()}>
          {rows.map((row, rowIndex) => {
            prepareRow(row);
            return (
              <TableRow {...row.getRowProps()} key={rowIndex} $clickable={!!onRowClick}>
                {row.cells.map((cell, cellIndex) => (
                  <TableDataCell
                    {...cell.getCellProps()}
                    key={cellIndex}
                    $align={cell.column.align}
                    onClick={cellClickHandler(cell)}
                  >
                    {cell.render('Cell')}
                  </TableDataCell>
                ))}
              </TableRow>
            );
          })}
        </TableBody>

        {hasFooter && data.length > 0 && (
          <TableFooter>
            <TableFooterRow>
              <TableFooterCell colSpan={columns.length}>
                <TablePaginationWrapper>
                  <PaginationTitle>
                    Showing{' '}
                    <strong>
                      {start}–{end}
                    </strong>{' '}
                    of <strong>{total}</strong>
                  </PaginationTitle>
                  <PaginationControls>
                    <PaginationLeft>
                      <PaginationButton
                        type="button"
                        onClick={hasPrev ? prevPage : undefined}
                        disabled={!hasPrev}
                      >
                        <ChevronLeftIcon />
                        Previous
                      </PaginationButton>
                    </PaginationLeft>
                    <PaginationRight>
                      <PaginationButton
                        type="button"
                        onClick={hasNext ? nextPage : undefined}
                        disabled={!hasNext}
                      >
                        Next
                        <ChevronRightIcon />
                      </PaginationButton>
                    </PaginationRight>
                  </PaginationControls>
                </TablePaginationWrapper>
              </TableFooterCell>
            </TableFooterRow>
          </TableFooter>
        )}
      </StyledTable>
    </TableScrollWrapper>
  );
});

Table.displayName = 'Table';

export default Table;
